import { FormAddress } from '@user/components/Formik'
import { ITargetPest } from '@user/store/targetPests/typings'
import { LeadFilterSet } from './filters'
import type {
  CardClosingRequirements,
  CustomFieldValueInput,
  ENTITY_TYPES,
  EntityLocations,
  IActivityDTO,
  IContact,
  ICustomFieldValueInput,
  IEmailCollectionItem,
  IEntityContactDTO,
  IEntityDetails,
  ILocation,
  IODataCustomValues,
  IServiceDTO,
  IUser,
} from '@sales-center/common'
import { IContactDTO, IContactInput } from '../contacts/typings'
import type { ICloseRequirements } from '../opportunity/typings'
import { CardListViewMode } from '@sales-center/components'
import { ServiceLocationListViewDTO } from '../locations/typings'
import { IScheduleSettingsDetail } from '../closingRequirements/typings'
import { ICallTrackingActivityDetail } from '@user/features/CampaignTracking/typings'
import { IAgreementStatus } from '../agreements/typings'
import { ILocation as IServiceLocation } from '@user/store/locations/typings'

export type LeadPageState = {
  count: number
  page: number
  perPage: number
  loading: boolean
  shouldFetchList: boolean
  filterSet: LeadFilterSet
  detail?: ILeadDetail
  mode: CardListViewMode
  multipleServiceLocations: boolean
}

export interface ILead extends IODataCustomValues {
  autoReviewed: boolean
  missingCompanyRequiredFields?: ILeadMissingFieldDTO[]
  assigneeId: string | null
  assignee: IUser | null
  attachmentCount: number
  phone: string
  phoneExt: string | null
  mobilePhone: string
  emailAddress: string
  externalLocationId: string
  externalLocationCode: string
  firstName: string
  id: number
  lastName: string
  company: string
  leadName: string
  salesFunnelId: number
  salesFunnelStageId: number
  salesTeamId: number
  salesTeamIds?: number[]
  positionId: number
  nextActionDate: Date | null
  disqualifiedDate: Date | null
  totalLeadValue: number
  initialValue: number
  recurringValue: number
  createdBy: IUser | null
  submittedBy: string | null
  createdDate: Date
  updatedBy: IUser | null
  updatedDate: Date | null
  closedDate: Date | null
  branchId: string
  branch: string
  address: string
  address2: string
  city: string
  state: string
  postalCode: string
  billToContactName: string
  billToEmail: string
  billToPhone: string
  billToMobilePhone: string
  billToAddress: string
  billtoPostalCode: string
  externalBillToCode: string
  externalBillToId: string
  billToDivisionId: string
  billToLocationTypeId: string
  locationTypeId: string
  servicesCount: number
  services: IServiceDTO[]
  contactName: string
  divisionId: string
  sourceId: string
  campaignId: string | null
  closed: boolean
  closingRequirements: CardClosingRequirements
  primaryContact: IEntityContactDTO
  primaryContactId: number
  serviceLocationsCount: number
  billToContact: IContact
  referralDiscountId: string
  referralDiscountCode: string
  referralLocationCode: string
  referralLocationId: string
  serviceLocation: ServiceLocationListViewDTO
  primaryContactSearchName: string
  age: number
  scheduleSettings?: IScheduleSettingsDetail
  hasValidLocationAddress: boolean
  activities: IActivityDTO[]
  type: string
  primaryContactSearchAddress: string
  primaryContactSearchPhone: string
  primaryContactSearchMobile: string
  primaryContactSearchPrimaryEmail: string
  callTrackingEventId: string
  importId?: number
  targetPests?: ITargetPest[]
  targetPestIds?: number[]
  agreementStatusIds: string[]
  agreementStatuses: IAgreementStatus[]
  hasPestPacLocation: boolean
  serviceContact: IContact
  hasLocationDetails: boolean
  locations: EntityLocations
  hasValidPropertyAddress: boolean
  customerReferralLocationId: string
  customerReferralLocationCode: string
  employeeReferral: string
  otherReferral: string
  knockStatus?: string | null
  knockStatusReason?: string | null
  knockStatusSettingId?: number | null
}

export interface ILeadStatusUpdate {
  leadId: number
  reasonId: number // zero for custom reason
  customReason: string
  disqualifiedDate: Date | null
  owner?: string | null
  salesFunnelStageId: number
  salesFunnelStageName: string
  age?: number
  notServicedReason?: string
}

export interface ILeadNextActionDateUpdate {
  leadId: number
  nextActionDate: Date | null
}

export interface ILeadReassignUpdate {
  leadId: number
  salesTeamId: number
  assigneeId: string
}

export interface ILeadAccountCreationCard {
  createdDate: Date
  locationType: string
  salesTeamName: string
}

export interface ILeadInput extends ICustomFieldValueInput {
  assigneeId: string | null
  automatedAssignmentRuleId?: number
  callTrackingSystemFieldValues?: ICallTrackingActivityDetail[] | null
  campaign: string | null
  campaignId: string | null
  divisionId: string
  division: string
  draftLeadId?: number
  draftNote?: string
  leadSource: string | null
  leadSourceId: string | null
  externalLocationCode?: string | null
  externalLocationId?: string | null
  contactLocationId?: number
  knockStatusLocationId?: number
  locations: ILeadLocationInput[]
  locationSameAsBillTo: boolean
  locationTypeId: string
  locationType: string
  nextActionDate: Date | null
  note: string
  originalContactMethod: string
  preferredMethodOfContact: string
  preferredTimeOfContact: string
  referralSource?: string
  referralType?: 'Employee' | 'Customer' | 'Other' | 'None'
  referralValue?: string
  referralDiscountId?: string
  referralDiscountCode?: string
  referralDiscountType?: 'dollar' | 'percentage'
  referralDiscountValue?: number
  salesFunnelId: number
  salesFunnelStageId: number
  salesTeamId: number
  reasonId?: number // zero for custom reason
  customReason?: string
  submittedBy?: string
  tags: string
  targetPests: string
  type: string
  updateCoreLocation?: boolean
  viewDetail?: boolean
  noteCode?: string
  addNoteToPestPac?: boolean
  importId?: number
  importName: string
  createNewLocation?: boolean
}

export interface ILeadLocationInput {
  primaryServiceLocation: boolean
  billingLocation: boolean
  contact: ILeadContactInput
  divisionId: string
  division: string
  locationTypeId: string
  locationType: string
}

export interface ILeadContactInput {
  firstName: string
  lastName: string
  title: string
  company: string
  emailAddresses: IEmailCollectionItem[]
  phoneNumber: string
  phoneExt: string
  mobilePhone: string
  altPhoneNumber: string
  altPhoneExt: string
  street1: string
  street2: string
  city: string
  stateAbbreviation: string
  postalCode: string
  county: string
  latitude: number
  longitude: number
  externalLinkId: string
  externalLinkType: string
  contactTime: string
  contactMethod: string
  locationCode?: string
  externalLocationCode?: string
  externalLocationId?: string
}

export interface ILeadDTO {
  id: number
  tenantId: string
  salesTeamId: number
  assigneeId: string | null
  assignee: IUser | null
  leadSourceId: number
  campaignId: number
  status: string
  preferredMethodOfContact: string
  preferredTimeOfContact: string
  nextActionDate: Date | null
  targetPests: string
  tags: string
  note: string
  locationTypeId: string
  divisionId: number | null
  leadType: string
  userId: number | null
  userFirstName: string | null
  branchId: string
  branchName: string
  createdBy: IUser | null
  salesTeamName: string
  locationType: string
  createdDate: Date
  secondaryContacts: IContact[]
  locations: ILocation[]
  referralSource: string
  referralType: 'Employee' | 'Customer' | 'Other'
  referralValue: string
  referralDiscountId: string
  referralDiscountCode: string
  referralDiscountType?: 'dollar' | 'percentage'
  referralDiscountValue: number
  draftAgreementsUpdated: boolean
  serviceLocation?: IServiceLocation
}

export function ContactToFormAddress(contact: IContact): FormAddress {
  return {
    address1: contact.street1,
    address2: contact.street2,
    city: contact.city,
    state: contact.stateAbbreviation,
    postalCode: contact.postalCode,
    county: contact.county,
    country: contact.country,
    lat: contact.latitude,
    lng: contact.longitude,
  }
}

export interface ILeadDetail extends IEntityDetails {
  autoReviewed: boolean
  missingCompanyRequiredFields?: ILeadMissingFieldDTO[]
  scheduleSettings: IScheduleSettingsDetail
  entityType: ENTITY_TYPES
}

export function isLeadDetail(
  entity: ILead | ILeadDetail,
): entity is ILeadDetail {
  return !!(entity as ILeadDetail).billToContact
}

export const fixLeadDetailDates = (payload: ILeadDetail): ILeadDetail => ({
  ...payload,
  closedDate: payload.closedDate ? new Date(payload.closedDate) : null,
  createdTime: new Date(payload.createdTime),
  nextActionDate: payload.nextActionDate
    ? new Date(payload.nextActionDate)
    : null,
  updatedDate: payload.updatedDate ? new Date(payload.updatedDate) : null,
})

export interface LeadInfo extends ICustomFieldValueInput {
  id: number
  assigneeId: string | null
  campaignId: string | null
  campaign: string | null
  leadSourceId: string | null
  leadSource: string | null
  locationTypeId: string
  salesFunnelId: number
  salesTeamId: number
  targetPests: ITargetPest[]
  draftAgreementsUpdated?: boolean
}

export interface LeadName {
  id: number
  contactId: number
  company: string
  firstName: string
  lastName: string
  draftAgreementsUpdated?: boolean
}

export interface ISalesTeamReassignUpdate {
  id: number
  salesTeamId: number
  assigneeId: string | null
}

export interface ILeadContactAttach {
  contactId: number
  leadId: number
}

export interface ILeadContactUpdate {
  leadId: number
  isPrimaryContact: boolean
  contact: IContactInput
}

export interface ILeadContactUpdateResponse {
  leadId: number
  isPrimaryContact: boolean
  contact: IContactDTO
}

export interface ILeadContactDeleteModel {
  leadId: number
  contactId: number
}

export interface IPrimaryContactModel {
  id: number
  contactId: number
  externalLinkId: string
  isExternalContact: boolean
  externalLocationCode: string
  draftAgreementsUpdated?: boolean
}

export interface ISalesTeamReassignDTO {
  id: number
  salesTeamId: number
  assigneeId: string | null
  assignee: IUser | null
  salesTeamName: string
}

export interface IExportInput {
  columns: string[]
  multiServiceLocations: boolean
}

export type ICrossBranchReassign = {
  crossBranchReassignEnabled: boolean
  hasServices: boolean
  isMatched: boolean
  isClosed: boolean
}

export enum HistoryRoutePathnames {
  DRAFT_LEAD_ADD = '/leads/addLead?draftId=',
  DRAFT_LEADS = '/pending-leads',
  LEADS = '/leads',
  LEAD_DETAILS = '/leads/',
  OPPORTUNITIES = '/opportunities',
  OPPORTUNITY_DETAILS = '/opportunities/',
}

export interface ILeadScheduleAutoClosable {
  closeable: boolean
  requirements: ICloseRequirements[]
  missingScheduleAutoCloseRequiredFields: number[]
  funnelScheduleAutoCloseApplicable: boolean
}

/** A company-required lead field that an auto-reviewed lead is still missing. */
export interface ILeadMissingFieldDTO {
  fieldKey: string | null
  fieldId: number
  fieldName: string
  isCustomField: boolean
}

/** Values from the "Update Required Fields" modal; only provided keys are saved. */
export interface ILeadCompanyRequiredFieldsInput {
  divisionId?: string
  division?: string
  locationTypeId?: string
  locationType?: string
  campaignId?: string
  campaign?: string
  leadSourceId?: string
  leadSource?: string
  preferredMethodOfContact?: string
  preferredTimeOfContact?: string
  nextActionDate?: Date | string
  altPhoneNumber?: string
  street1?: string
  street2?: string
  city?: string
  stateAbbreviation?: string
  postalCode?: string
  note?: string
  targetPestIds?: string[]
  customFieldValues?: CustomFieldValueInput[]
}
